/* @ts-expect-error TS1192 */
import type DashboardVizProps from '@splunk/visualizations/src/common/types/DashboardVizProps';
// @ts-ignore
import OptionsEvaluator from './OptionsEvaluator.worker.ts';

export type ColumnVal = number | string | string[];
interface ColumnarData {
    data: {
        fields: { name: string; type?: string }[] | string[];
        columns: ColumnVal[][];
    };
}

interface PromiseInterface {
    resolve: (value: Record<string, unknown> | PromiseLike<Record<string, unknown>>) => void;
    reject: (reason?: any) => void;
}

export interface VisualizationOptionsEvaluation {
    visualizationID: string;
    options?: Record<string, any>;
    promise?: PromiseInterface;
    state: State;
}

export interface AsyncDynamicOptionsEvaluatorParams {
    visualizationID: string;
    props: DashboardVizProps;
    options: Record<string, unknown>;
    dataSources: { [key: string]: ColumnarData };
    theme: unknown;
    bypassCloneDeepOptionScope?: boolean;
    lazyLoadingDataFrame?: boolean;
    enableTagSearchResult?: boolean;
}

enum State {
    Backlog,
    Processing,
    Complete,
}

class AsyncDynamicOptionsEvaluator {
    static workerMap: Record<string, VisualizationOptionsEvaluation> = {}; // NOSONAR - mutated at runtime

    static optionsEvaluator; // NOSONAR - mutated at runtime

    static isOptionsEvaluatorBusy = false; // NOSONAR - mutated at runtime

    static lastResolved: any = {}; // NOSONAR - mutated at runtime (used for testing and debugging)

    public static getWorker(): Worker {
        return AsyncDynamicOptionsEvaluator.optionsEvaluator;
    }

    static createWorker(): void {
        try {
            AsyncDynamicOptionsEvaluator.optionsEvaluator = new OptionsEvaluator();
            AsyncDynamicOptionsEvaluator.optionsEvaluator.onmessage =
                AsyncDynamicOptionsEvaluator.onOptionsEvaluatorMessage;
        } catch (error) {
            // this try catch is needed for Jest tests
        }
    }

    static evaluate(
        asyncDynamicOptionsEvaluatorParams: AsyncDynamicOptionsEvaluatorParams
    ): Promise<Record<string, unknown>> {
        const {
            visualizationID,
            props,
            options,
            dataSources,
            theme,
            bypassCloneDeepOptionScope,
            lazyLoadingDataFrame,
            enableTagSearchResult,
        } = asyncDynamicOptionsEvaluatorParams;
        if (!AsyncDynamicOptionsEvaluator.getWorker()) {
            AsyncDynamicOptionsEvaluator.createWorker();
        }

        // create a new entry if its missing within the workerMap
        if (!AsyncDynamicOptionsEvaluator.workerMap[visualizationID]) {
            AsyncDynamicOptionsEvaluator.workerMap[visualizationID] = {
                visualizationID,
                options: null,
                state: State.Backlog,
            };
        }
        const workerMapEntry = AsyncDynamicOptionsEvaluator.workerMap[visualizationID];

        // flatten the themeFunc to a resolved object because functions can not be sent to a webworker
        const themeFuncResolved = {};
        Object.keys(theme).forEach(key => {
            themeFuncResolved[key] = theme[key](props);
        });

        const optionsToEvaluate = {
            message: 'evaluate',
            visualizationID,
            options,
            dataSources,
            themeFuncResolved,
            bypassCloneDeepOptionScope,
            lazyLoadingDataFrame,
            enableTagSearchResult,
        };

        workerMapEntry.options = optionsToEvaluate;
        workerMapEntry.state = State.Processing;

        // send that data to the worker if its not running
        if (!AsyncDynamicOptionsEvaluator.isOptionsEvaluatorBusy) {
            return new Promise((resolve, reject) => {
                workerMapEntry.promise = { resolve, reject };
                AsyncDynamicOptionsEvaluator.isOptionsEvaluatorBusy = true;
                AsyncDynamicOptionsEvaluator.getWorker().postMessage(optionsToEvaluate);
            });
        }

        workerMapEntry.state = State.Backlog;

        // return the promise for next time the worker is complete
        return new Promise((resolve, reject) => {
            workerMapEntry.promise = { resolve, reject };
        });
    }

    static onOptionsEvaluatorMessage(messageEventMessage: Record<string, any>): void {
        const { visualizationID, error } = messageEventMessage.data;

        const workerMapEntry = AsyncDynamicOptionsEvaluator.workerMap[visualizationID];

        if (error) {
            workerMapEntry.promise.reject({ error: `Error evaluating options for ${visualizationID}` });
        } else if (workerMapEntry?.state === State.Processing) {
            AsyncDynamicOptionsEvaluator.lastResolved = {
                visualizationID,
                ...messageEventMessage.data,
            };
            workerMapEntry.promise.resolve({
                visualizationID,
                ...messageEventMessage.data,
            });
            workerMapEntry.state = State.Complete;
        }

        AsyncDynamicOptionsEvaluator.isOptionsEvaluatorBusy = false;
        AsyncDynamicOptionsEvaluator.evaluateNext();
    }

    static reset(visualizationID: string): void {
        delete AsyncDynamicOptionsEvaluator.workerMap[visualizationID];
        AsyncDynamicOptionsEvaluator.evaluateNext();
    }

    static evaluateNext(): void {
        // remove completed tasks
        Object.keys(AsyncDynamicOptionsEvaluator.workerMap).forEach(key => {
            if (AsyncDynamicOptionsEvaluator.workerMap[key].state === State.Complete) {
                delete AsyncDynamicOptionsEvaluator.workerMap[key];
            }
        });

        // find the next item in workerMapEntry that has options and a promise, if one is found then we have more more data to process, send the options for processing the other thread and set its options to null
        const nextKey = Object.keys(AsyncDynamicOptionsEvaluator.workerMap).find(
            (key: string): boolean => AsyncDynamicOptionsEvaluator.workerMap[key].state === State.Backlog
        );

        if (nextKey && !AsyncDynamicOptionsEvaluator.isOptionsEvaluatorBusy) {
            AsyncDynamicOptionsEvaluator.isOptionsEvaluatorBusy = true;
            const { options } = AsyncDynamicOptionsEvaluator.workerMap[nextKey];
            AsyncDynamicOptionsEvaluator.workerMap[nextKey].state = State.Processing;
            AsyncDynamicOptionsEvaluator.getWorker().postMessage(options);
        }
    }
}

export default AsyncDynamicOptionsEvaluator;
